import * as React from "react";
import {
  Field,
  Input,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { FilterChipWrapper } from "./FilterChipWrapper";

// ── Dual-handle range slider (pure CSS + two native inputs) ──────────────

const useSliderStyles = makeStyles({
  container: {
    position: "relative",
    height: "32px",
    width: "100%",
    marginTop: "4px",
    marginBottom: "4px",
  },
  track: {
    position: "absolute",
    top: "50%",
    left: "0",
    right: "0",
    height: "4px",
    borderRadius: "2px",
    backgroundColor: tokens.colorNeutralStroke1,
    transform: "translateY(-50%)",
    pointerEvents: "none",
  },
  /** Filled range between the two handles */
  fill: {
    position: "absolute",
    top: "50%",
    height: "4px",
    borderRadius: "2px",
    backgroundColor: tokens.colorBrandBackground,
    transform: "translateY(-50%)",
    pointerEvents: "none",
  },
  /** Shared styles for both native range inputs (stacked on top of each other) */
  input: {
    position: "absolute",
    top: "0",
    left: "0",
    width: "100%",
    height: "100%",
    margin: "0",
    padding: "0",
    backgroundColor: "transparent",
    pointerEvents: "none",
    appearance: "none",
    WebkitAppearance: "none",
  },
});

/**
 * Custom CSS for range input thumbs — injected once via <style> tag.
 * Griffel doesn't support pseudo-element selectors, so we use a real stylesheet.
 */
const RANGE_THUMB_CLASS = "numeric-range-thumb";
const RANGE_THUMB_STYLE_ID = "numeric-range-thumb-style";

function ensureThumbStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById(RANGE_THUMB_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = RANGE_THUMB_STYLE_ID;
  style.textContent = `
    .${RANGE_THUMB_CLASS}::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: var(--colorBrandBackground, #0078d4);
      border: 2px solid white;
      box-shadow: 0 0 2px rgba(0,0,0,0.3);
      cursor: pointer;
      pointer-events: auto;
      position: relative;
      z-index: 2;
    }
    .${RANGE_THUMB_CLASS}::-moz-range-thumb {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: var(--colorBrandBackground, #0078d4);
      border: 2px solid white;
      box-shadow: 0 0 2px rgba(0,0,0,0.3);
      cursor: pointer;
      pointer-events: auto;
      position: relative;
      z-index: 2;
    }
    .${RANGE_THUMB_CLASS}::-webkit-slider-runnable-track {
      background: transparent;
    }
    .${RANGE_THUMB_CLASS}::-moz-range-track {
      background: transparent;
    }
  `;
  document.head.appendChild(style);
}

interface DualRangeSliderProps {
  min: number;
  max: number;
  step: number;
  valueLow: number;
  valueHigh: number;
  onLowChange: (v: number) => void;
  onHighChange: (v: number) => void;
}

const DualRangeSlider: React.FC<DualRangeSliderProps> = ({
  min,
  max,
  step,
  valueLow,
  valueHigh,
  onLowChange,
  onHighChange,
}) => {
  const styles = useSliderStyles();

  React.useEffect(() => {
    ensureThumbStyles();
  }, []);

  const range = max - min || 1;
  const leftPct = ((valueLow - min) / range) * 100;
  const rightPct = ((valueHigh - min) / range) * 100;

  return (
    <div className={styles.container}>
      {/* Background track */}
      <div className={styles.track} />
      {/* Filled range */}
      <div
        className={styles.fill}
        style={{ left: `${leftPct}%`, width: `${rightPct - leftPct}%` }}
      />
      {/* Low handle */}
      <input
        type="range"
        className={`${styles.input} ${RANGE_THUMB_CLASS}`}
        min={min}
        max={max}
        step={step}
        value={valueLow}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          onLowChange(Math.min(v, valueHigh));
        }}
        style={{ zIndex: valueLow > max - (max - min) / 2 ? 3 : 1 }}
      />
      {/* High handle */}
      <input
        type="range"
        className={`${styles.input} ${RANGE_THUMB_CLASS}`}
        min={min}
        max={max}
        step={step}
        value={valueHigh}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          onHighChange(Math.max(v, valueLow));
        }}
        style={{ zIndex: 2 }}
      />
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────────

const useStyles = makeStyles({
  rangeInputs: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
  },
  separator: {
    fontWeight: "600",
    paddingTop: "0.25rem",
    paddingBottom: "0.25rem",
  },
  bounds: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    marginTop: "2px",
  },
});

export interface NumericFilterStats {
  min: number;
  max: number;
  null_count?: number;
}

export interface NumericRangeFilterProps {
  label: string;
  variable: string;
  stats: NumericFilterStats;
  /** Persisted applied value — `{variable_min: n, variable_max: n}` or `null`. */
  value?: Record<string, number> | null;
  /** Whether this filter has been modified from its default */
  isModified?: boolean;
  /** Called when the user clicks the reset icon */
  onReset?: () => void;
  /** Called with `{variable_min: n, variable_max: n}` or `null` (full range). */
  onApply: (value: Record<string, number> | null) => void;
}

/** Round to 2 decimals for display. */
function displayNum(n: number): string {
  if (Number.isInteger(n)) return String(n);
  const rounded = Math.round(n * 100) / 100;
  return String(rounded);
}

/**
 * Numeric range filter — chip button with popover containing
 * min/max inputs and a dual-handle range slider.
 *
 * Uses filter_stats.min / filter_stats.max for bounds.
 * No server queries — Apply only.
 */
export const NumericRangeFilter: React.FC<NumericRangeFilterProps> = ({
  label,
  variable,
  stats,
  value: persistedValue,
  isModified = false,
  onReset,
  onApply,
}) => {
  const styles = useStyles();
  const { min: statsMin, max: statsMax } = stats;

  // Hide if single value
  if (statsMin === statsMax) return null;

  // Restore from persisted value when available
  const initMin = persistedValue?.[`${variable}_min`] ?? null;
  const initMax = persistedValue?.[`${variable}_max`] ?? null;

  const [appliedMin, setAppliedMin] = React.useState<number | null>(initMin);
  const [appliedMax, setAppliedMax] = React.useState<number | null>(initMax);
  const [draftMin, setDraftMin] = React.useState(initMin ?? statsMin);
  const [draftMax, setDraftMax] = React.useState(initMax ?? statsMax);

  const isAllApplied = appliedMin === null && appliedMax === null;

  // Sync internal state when parent resets (isModified goes false while we have applied values)
  const prevIsModified = React.useRef(isModified);
  React.useEffect(() => {
    if (prevIsModified.current && !isModified) {
      // Parent reset — clear applied values and reset draft to full range
      setAppliedMin(null);
      setAppliedMax(null);
      setDraftMin(statsMin);
      setDraftMax(statsMax);
    }
    prevIsModified.current = isModified;
  }, [isModified, statsMin, statsMax]);

  // Slider step: use 1 for integers, 0.01 for floats
  const step = Number.isInteger(statsMin) && Number.isInteger(statsMax) ? 1 : 0.01;

  function apply() {
    const isFullRange = draftMin <= statsMin && draftMax >= statsMax;
    if (isFullRange) {
      setAppliedMin(null);
      setAppliedMax(null);
      onApply(null);
    } else {
      setAppliedMin(draftMin);
      setAppliedMax(draftMax);
      onApply({
        [`${variable}_min`]: draftMin,
        [`${variable}_max`]: draftMax,
      });
    }
  }

  function cancel() {
    setDraftMin(appliedMin ?? statsMin);
    setDraftMax(appliedMax ?? statsMax);
  }

  const isDirty =
    draftMin !== (appliedMin ?? statsMin) ||
    draftMax !== (appliedMax ?? statsMax);

  const chipValue = isAllApplied
    ? "All"
    : `${displayNum(appliedMin!)} – ${displayNum(appliedMax!)}`;

  return (
    <FilterChipWrapper
      label={label}
      displayValue={chipValue}
      isModified={isModified}
      onReset={onReset ?? (() => {})}
      isDirty={isDirty}
      onApply={apply}
      onCancel={cancel}
      surfaceWidth="300px"
    >
      <div className={styles.rangeInputs}>
        <Field label="Min" style={{ flex: 1 }}>
          <Input
            type="number"
            value={String(draftMin)}
            min={statsMin}
            max={draftMax}
            step={step}
            onChange={(_, data) => {
              const v = parseFloat(data.value);
              if (!isNaN(v)) setDraftMin(Math.max(statsMin, Math.min(v, draftMax)));
            }}
          />
        </Field>
        <span className={styles.separator}>—</span>
        <Field label="Max" style={{ flex: 1 }}>
          <Input
            type="number"
            value={String(draftMax)}
            min={draftMin}
            max={statsMax}
            step={step}
            onChange={(_, data) => {
              const v = parseFloat(data.value);
              if (!isNaN(v)) setDraftMax(Math.min(statsMax, Math.max(v, draftMin)));
            }}
          />
        </Field>
      </div>

      <DualRangeSlider
        min={statsMin}
        max={statsMax}
        step={step}
        valueLow={draftMin}
        valueHigh={draftMax}
        onLowChange={setDraftMin}
        onHighChange={setDraftMax}
      />

      <div className={styles.bounds}>
        <span>{displayNum(statsMin)}</span>
        <span>{displayNum(statsMax)}</span>
      </div>
    </FilterChipWrapper>
  );
};